import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Bot,
  CheckIcon,
  ChevronDownIcon,
  LoaderIcon,
  XCircleIcon,
  XIcon,
} from 'lucide-react';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { useAgentDisplayNameResolver } from '@cotera/client/app/hooks/use-agents-display-names';
import {
  ModelScopeFactory,
  useTargetedModel,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import {
  useAnalytics,
  useCocoConversationId,
  useChatSurface,
  CocoEvent,
} from '@cotera/client/app/etc';
import { CocoChatModel } from '../chat/models/coco-chat.model';
import {
  SubAgentToolModel,
  useSubAgentToolModel,
} from './sub-agent-tool.model';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@cotera/client/v0/components/ui/collapsible';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Alert, AlertDescription } from '@cotera/client/v0/components/ui/alert';
import { cn } from '@cotera/client/v0/lib/utils';
import type { ChatModel } from '@cotera/client/v0/chat/chat.model';
import { getV1ToolRenderer } from './v1-tool-renderers';
import type { MessageClass, ToolPart } from '@cotera/sdk/core';
import type {
  CustomToolRenderer,
  ToolRendererProps,
} from '@cotera/client/v0/chat/tool-renderers';
import { toast } from '@cotera/client/app/components/ui';
import { coco } from '@cotera/api';
import { formatCredits } from '@cotera/client/v0/components/topbar/credit-balance-section';
import { translateToolName } from './translate-tool-name';
import { lastToolPartFailed, toolPartFailed } from './tool-part-failed';
import { CodeBlock } from '@cotera/client/v0/components/ai-elements/code-block';
import { CodeBlockCopyButton } from '@cotera/client/v0/components/ui/code-block';
import {
  Tool,
  ToolHeader,
  ToolContent,
  ToolInput,
} from '@cotera/client/v0/components/ai-elements/tool';
import { RunningToolTitle } from '@cotera/client/v0/chat/running-tool-title';

function lowercaseFirst(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

const FADE_MS = 250;

/**
 * Fades out old text, swaps, fades in new text.
 */
export const FadeText: React.FC<{ text: string; className?: string }> = ({
  text,
  className,
}) => {
  const [displayed, setDisplayed] = useState(text);
  const [opacity, setOpacity] = useState(1);
  const pendingText = useRef(text);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (text === displayed) {
      return;
    }
    pendingText.current = text;
    // Fade out
    setOpacity(0);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setDisplayed(pendingText.current);
      // Small gap so browser paints opacity:0 with new text before fading in
      requestAnimationFrame(() => {
        setOpacity(1);
      });
    }, FADE_MS);
    return () => clearTimeout(timerRef.current);
  }, [text, displayed]);

  return (
    <span
      className={className}
      style={{
        opacity,
        transition: `opacity ${FADE_MS}ms ease-in-out`,
      }}
    >
      {displayed}
    </span>
  );
};

const SNIPPET_LINES = 1;
const SNIPPET_CHARS = 200;

export function filterDisplayableInput(
  input: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(input).filter(([k]) => !k.startsWith('@@'))
  );
}

function truncateForSnippet(str: string, maxChars: number): string {
  if (str.length <= maxChars) {
    return str;
  }
  return str.slice(0, maxChars) + '…';
}

function SeeMoreBlock({
  content,
  label = 'See more',
  labelLess = 'See less',
  defaultExpanded = false,
}: {
  content: string;
  label?: string;
  labelLess?: string;
  language?: string;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const lines = content.split('\n');
  const isLong = lines.length > SNIPPET_LINES || content.length > SNIPPET_CHARS;
  const displayContent =
    isLong && !expanded
      ? truncateForSnippet(
          lines.slice(0, SNIPPET_LINES).join('\n'),
          SNIPPET_CHARS
        )
      : content;

  return (
    <div className="space-y-1">
      <pre className="overflow-x-auto rounded-md bg-muted/50 p-2 text-xs">
        <code>{displayContent}</code>
      </pre>
      {isLong && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-muted-foreground"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? labelLess : label}
        </Button>
      )}
    </div>
  );
}

/**
 * Create a SubAgentToolRenderer.
 * Used by v1-tool-renderers to avoid JSX in .ts files.
 */
export function createSubAgentToolRenderer(): CustomToolRenderer {
  return (props) => (
    <SubAgentChatRenderer part={props.part} readonly={props.readonly} />
  );
}

function createReadonlyChatModel(
  conversationId: string
): Pick<
  ChatModel,
  'lastMessageId' | 'status' | 'addToolResult' | 'id' | 'model'
> {
  return {
    id: conversationId,
    get lastMessageId() {
      return undefined;
    },
    get status() {
      return 'ready' as const;
    },
    get model() {
      return '';
    },
    addToolResult(_toolCallId: string, _result: unknown) {
      // noop - readonly
    },
  };
}

export type SubAgentToolRendererProps = {
  part: ToolRendererProps['part'];
  readonly?: boolean;
};

export function collectToolParts(messages: MessageClass[]): ToolPart[] {
  const parts: ToolPart[] = [];
  for (const msg of messages) {
    if (msg.isAssistantMessage()) {
      parts.push(...msg.getToolParts());
    }
  }
  return parts;
}

export function getLastAssistantText(messages: MessageClass[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg === undefined || !msg.isAssistantMessage()) {
      continue;
    }
    const textParts = msg.parts.filter((p) => p.isTextPart());
    if (textParts.length > 0) {
      const text = textParts
        .map((p) => ('text' in p ? String((p as { text: string }).text) : ''))
        .join('');
      if (text.trim().length > 0) {
        return text;
      }
    }
  }
  return null;
}

const SubAgentTool: React.FC<SubAgentToolRendererProps> = ({
  part,
  readonly = true,
}) => {
  const client = useTenantedClient();
  const cocoChatModel = useTargetedModel(CocoChatModel);
  const subAgentToolModel = useSubAgentToolModel(part.toolCallId);
  const conversationId = subAgentToolModel.conversationId;
  const status = useWatchableValue(subAgentToolModel.statusWatchable);
  const messages = useWatchableValue(subAgentToolModel.messagesWatchable);
  const totalCredits = useWatchableValue(subAgentToolModel.creditsWatchable);

  const model =
    (cocoChatModel as CocoChatModel | null)?.model ?? coco.DEFAULT_MODEL;

  // Synchronous in-flight guard keyed by toolCallId. A ref mutates immediately,
  // so near-instant double-clicks are blocked before React re-renders (a
  // useState flag updates asynchronously and can let a second click through).
  // Keyed per tool so unrelated approvals can still run concurrently. The
  // state mirror only drives the disabled visual.
  const inFlightApprovalsRef = useRef<Set<string>>(new Set());
  const [inFlightApprovalIds, setInFlightApprovalIds] = useState<
    ReadonlySet<string>
  >(new Set());

  const approvalHandler = useCallback(
    async (params: {
      toolCallId: string;
      approve: boolean;
      reason?: string;
    }) => {
      if (conversationId === null) {
        return;
      }
      const { toolCallId } = params;
      if (inFlightApprovalsRef.current.has(toolCallId)) {
        return;
      }
      inFlightApprovalsRef.current.add(toolCallId);
      setInFlightApprovalIds(new Set(inFlightApprovalsRef.current));
      try {
        const result = await client.resource.chat.runTool({
          conversationId,
          toolCallId,
          model,
          action: params.approve ? 'approve' : 'deny',
          reason: params.reason,
        });
        if (result.isErr()) {
          const errorMessage =
            result.error.t === 'application'
              ? result.error.err.message
              : 'Failed to execute tool';
          toast.error(errorMessage);
          return;
        }
        void subAgentToolModel.snapshotWatchable.refresh();
      } finally {
        inFlightApprovalsRef.current.delete(toolCallId);
        setInFlightApprovalIds(new Set(inFlightApprovalsRef.current));
      }
    },
    [
      client.resource.chat,
      conversationId,
      model,
      subAgentToolModel.snapshotWatchable,
    ]
  );

  const toolKey = part.toolKey().toString();
  const resolveAgentName = useAgentDisplayNameResolver();

  const displayName =
    toolKey === 'coco/sub-agent'
      ? 'Sub Agent'
      : toolKey === 'coco/cotera-agent'
      ? 'Cotera Agent'
      : toolKey === 'coco/cotera-workspace'
      ? 'Cotera workspace'
      : toolKey === 'coco/folder-editor'
      ? 'Folder editor'
      : resolveAgentName(toolKey) ?? 'Sub Agent';

  const isRunning = status === 'streaming' || status === 'submitted';
  const chatModel = useMemo(
    () => createReadonlyChatModel(conversationId ?? ''),
    [conversationId]
  );

  const toolParts = useMemo(() => collectToolParts(messages), [messages]);
  const toolNeedingApproval = useMemo(
    () => toolParts.find((tp) => tp.approvalStatus() === 'required') ?? null,
    [toolParts]
  );
  const anyToolNeedsApproval = toolNeedingApproval !== null;
  const anyToolWasDenied = useMemo(
    () => toolParts.some((tp) => tp.approvalStatus() === 'denied'),
    [toolParts]
  );
  const lastToolFailed = useMemo(
    () => lastToolPartFailed(toolParts),
    [toolParts]
  );
  const lastResult = useMemo(() => getLastAssistantText(messages), [messages]);
  const displayableInput = useMemo(() => {
    const input = part.input;
    if (input === undefined || typeof input !== 'object') {
      return {};
    }
    return filterDisplayableInput(input as Record<string, unknown>);
  }, [part.input]);

  const hasInput = Object.keys(displayableInput).length > 0;
  const inputJson = JSON.stringify(displayableInput, null, 2);

  const hasSeenTools = useRef(false);
  const cachedToolParts = useRef(toolParts);
  if (toolParts.length > 0) {
    hasSeenTools.current = true;
    cachedToolParts.current = toolParts;
  }

  const narrative = useMemo(() => {
    if (anyToolNeedsApproval) {
      return `${displayName} needs approval`;
    }
    if (!isRunning && anyToolWasDenied) {
      return `${displayName} was stopped`;
    }
    if (!isRunning && lastToolFailed) {
      return `${displayName} ran into an issue`;
    }
    if (
      !isRunning &&
      (toolParts.length > 0 || lastResult !== null || status === 'ready')
    ) {
      return `${displayName} finished`;
    }
    // Find the last tool that's still running (no output yet)
    for (let i = toolParts.length - 1; i >= 0; i--) {
      const tp = toolParts[i]!;
      if (!tp.hasOutput || tp.isIncomplete()) {
        const action = translateToolName(tp.toolKey().toString(), 'running');
        return `${displayName} is ${lowercaseFirst(action)}`;
      }
    }
    if (hasSeenTools.current) {
      return `${displayName} is thinking…`;
    }
    return `Waking up ${displayName}…`;
  }, [
    isRunning,
    toolParts,
    displayName,
    anyToolNeedsApproval,
    anyToolWasDenied,
    lastToolFailed,
    lastResult,
    status,
  ]);

  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (anyToolNeedsApproval) {
      setIsOpen(true);
    }
  }, [anyToolNeedsApproval]);

  if (conversationId === null) {
    // Only spin while the tool is still initializing. Once completed or denied,
    // show a static terminal state so the spinner doesn't persist forever.
    if (!part.isIncomplete() || part.isDenied()) {
      const isDenied = part.isDenied();
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
          {isDenied ? (
            <XIcon className="h-4 w-4 shrink-0 text-orange-600" />
          ) : (
            <CheckIcon className="h-4 w-4 shrink-0 text-green-600" />
          )}
          <span className="italic text-sm">
            {isDenied
              ? `${displayName} was stopped`
              : `${displayName} finished`}
          </span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
        <LoaderIcon className="h-4 w-4 animate-spin" />
        <RunningToolTitle className="italic text-sm">
          Waking up {displayName}…
        </RunningToolTitle>
      </div>
    );
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="group not-prose w-full"
    >
      <div className="flex items-center gap-2 py-1">
        <CollapsibleTrigger className="flex flex-1 items-center justify-between gap-2 text-left min-w-0">
          <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
            <Bot className="h-4 w-4 shrink-0" />
            {isRunning ? (
              <LoaderIcon className="h-4 w-4 shrink-0 animate-spin" />
            ) : anyToolWasDenied || lastToolFailed ? (
              <XIcon className="h-4 w-4 shrink-0 text-orange-600" />
            ) : (
              <CheckIcon className="h-4 w-4 shrink-0 text-green-600" />
            )}
            {isRunning ? (
              <RunningToolTitle className="italic text-sm min-w-0">
                <FadeText text={narrative} />
              </RunningToolTitle>
            ) : (
              <FadeText text={narrative} className="italic" />
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {totalCredits > 0 && (
              <span className="text-xs text-muted-foreground/70 font-normal not-italic whitespace-nowrap shrink-0">
                {isRunning ? '~' : ''}
                {formatCredits(totalCredits)} credits
              </span>
            )}
            <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </div>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent
        className={cn(
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2',
          'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:slide-in-from-top-2'
        )}
      >
        <div className="mt-2 rounded-md border border-border bg-muted/30 px-3 py-2 overflow-hidden space-y-3">
          {/* Input */}
          {hasInput && (
            <div className="space-y-1">
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Input
              </h4>
              <SeeMoreBlock
                content={inputJson}
                label="See more"
                labelLess="See less"
              />
            </div>
          )}

          {/* Tools - only show when there are tools or still running */}
          {(toolParts.length > 0 || isRunning || hasSeenTools.current) && (
            <div className="space-y-1">
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Tools
              </h4>
              <ul className="rounded-md border border-border divide-y divide-border overflow-hidden">
                {cachedToolParts.current.map((toolPart) => (
                  <ToolListItem
                    key={toolPart.toolCallId}
                    toolPart={toolPart}
                    chatModel={chatModel}
                    readonly={readonly}
                    approvalHandler={approvalHandler}
                  />
                ))}
                {isRunning &&
                  toolParts.length === 0 &&
                  !hasSeenTools.current && (
                    <li className="flex items-center gap-2 text-sm text-muted-foreground px-3 py-2">
                      <LoaderIcon className="h-3 w-3 animate-spin" />
                      <span>Waiting for tools…</span>
                    </li>
                  )}
              </ul>
            </div>
          )}

          {/* Approval */}
          {toolNeedingApproval !== null && readonly !== true && (
            <Tool defaultOpen>
              <ToolHeader
                state={'input-available'}
                title={translateToolName(
                  toolNeedingApproval.toolKey().toString(),
                  'waiting'
                )}
                type={toolNeedingApproval.toolPart.type}
              />
              <ToolContent>
                <ToolInput input={toolNeedingApproval.input} />
                <div className="px-4 pb-2">
                  <Alert className="flex flex-col gap-2 bg-card/60">
                    <AlertDescription className="inline">
                      <div className="mb-2">
                        Tool{' '}
                        <strong>
                          {toolNeedingApproval.toolKey().toString()}
                        </strong>{' '}
                        requires approval to execute.
                      </div>
                      <div className="flex items-center justify-end gap-2 self-end">
                        <Button
                          className="h-8 px-3 text-sm"
                          type="button"
                          variant="outline"
                          disabled={inFlightApprovalIds.has(
                            toolNeedingApproval.toolCallId
                          )}
                          onClick={() => {
                            void approvalHandler({
                              toolCallId: toolNeedingApproval.toolCallId,
                              approve: false,
                              reason: 'User denied tool execution',
                            });
                          }}
                        >
                          Reject
                        </Button>
                        <Button
                          className="h-8 px-3 text-sm"
                          type="button"
                          variant="default"
                          disabled={inFlightApprovalIds.has(
                            toolNeedingApproval.toolCallId
                          )}
                          onClick={() => {
                            void approvalHandler({
                              toolCallId: toolNeedingApproval.toolCallId,
                              approve: true,
                            });
                          }}
                        >
                          Accept
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                </div>
              </ToolContent>
            </Tool>
          )}

          {/* Result */}
          {lastResult !== null && (
            <div
              className="space-y-1 animate-in fade-in duration-300"
              ref={(el) => {
                if (el !== null) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
              }}
            >
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Result
              </h4>
              <SeeMoreBlock
                content={lastResult}
                label="See more"
                labelLess="See less"
                language="text"
              />
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const ToolListItem: React.FC<{
  toolPart: ToolPart;
  chatModel: Pick<ChatModel, 'id' | 'model' | 'addToolResult'>;
  readonly?: boolean;
  approvalHandler?: (params: {
    toolCallId: string;
    approve: boolean;
    reason?: string;
  }) => Promise<void>;
}> = ({ toolPart, chatModel, readonly }) => {
  const toolName = toolPart.toolKey().toString();
  const CustomRenderer = getV1ToolRenderer(toolName);
  const isSubAgent = CustomRenderer !== null;
  const isDenied = toolPart.approvalStatus() === 'denied';
  const isComplete = toolPart.hasOutput && !toolPart.isIncomplete();
  const output = toolPart.getOutput();
  const analytics = useAnalytics();
  const cocoConversationId = useCocoConversationId();
  const surface = useChatSurface() ?? 'scratch';
  const hasError = isComplete && toolPartFailed(toolPart);
  const isFailed = isDenied || hasError;
  const stepState = isComplete || isFailed ? 'done' : ('running' as const);
  const humanLabel = isDenied
    ? `${translateToolName(toolName, 'waiting')} — rejected`
    : translateToolName(toolName, stepState);
  const rawPart = toolPart.toolPart;

  if (isSubAgent) {
    const Renderer = CustomRenderer!;
    return (
      <li className="list-none">
        <Renderer part={toolPart} chatModel={chatModel} readonly={readonly} />
      </li>
    );
  }

  const inputStr = JSON.stringify(
    filterDisplayableInput((rawPart.input as Record<string, unknown>) ?? {}),
    null,
    2
  );
  const outputStr =
    output !== undefined
      ? typeof output === 'string'
        ? output
        : JSON.stringify(output, null, 2)
      : '';

  return (
    <li className="list-none">
      <Collapsible className="group/tool">
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50">
          <div className="flex items-center gap-2 text-sm italic text-muted-foreground">
            <span>{humanLabel}</span>
            {isFailed ? (
              <XCircleIcon className="h-3.5 w-3.5 shrink-0 text-orange-600" />
            ) : isComplete ? (
              <CheckIcon className="h-3.5 w-3.5 shrink-0 text-green-600" />
            ) : (
              <LoaderIcon className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
            )}
          </div>
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/tool:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-2 px-4 pb-2">
            <div className="text-xs text-muted-foreground/60 font-mono">
              {toolName}
            </div>
            <div>
              <h5 className="text-xs font-medium text-muted-foreground mb-1">
                Parameters
              </h5>
              <div className="rounded-md bg-muted/50">
                <CodeBlock code={inputStr} language="json">
                  <CodeBlockCopyButton
                    onCopy={() => {
                      if (cocoConversationId !== null) {
                        analytics.track(CocoEvent.CodeBlockCopied, {
                          conversationId: cocoConversationId,
                          surface,
                          language: 'json',
                          source: 'sub-agent-tool-input',
                        });
                      }
                    }}
                  />
                </CodeBlock>
              </div>
            </div>
            {outputStr !== '' && (
              <div>
                <h5 className="text-xs font-medium text-muted-foreground mb-1">
                  Result
                </h5>
                <div className="rounded-md bg-muted/50">
                  <CodeBlock code={outputStr} language="json">
                    <CodeBlockCopyButton
                      onCopy={() => {
                        if (cocoConversationId !== null) {
                          analytics.track(CocoEvent.CodeBlockCopied, {
                            conversationId: cocoConversationId,
                            surface,
                            language: 'json',
                            source: 'sub-agent-tool-output',
                          });
                        }
                      }}
                    />
                  </CodeBlock>
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
};

const SubAgentChatRenderer = React.memo<{
  part: ToolRendererProps['part'];
  readonly?: boolean;
}>(
  ({ part, readonly }) => {
    const client = useTenantedClient();
    const watchablesFactory = useWatchablesFactory();
    const eventConfig = useMemo(
      () => ({ client: client.resource.chat }),
      [client.resource.chat]
    );
    const createModels = useCallback(
      () => [new SubAgentToolModel({ part, eventConfig, watchablesFactory })],
      [part, eventConfig, watchablesFactory]
    );
    return (
      <ModelScopeFactory createModels={createModels} deps={[part]}>
        <SubAgentTool part={part} readonly={readonly} />
      </ModelScopeFactory>
    );
  },
  (prev, next) => {
    // Compare the actual data that matters - toolCallId, input, and output
    // ToolPart uses getters which are not enumerable, so JSON.stringify won't work
    const prevOutput = prev.part.getOutput();
    const nextOutput = next.part.getOutput();
    const outputsEqual =
      prevOutput === nextOutput ||
      JSON.stringify(prevOutput) === JSON.stringify(nextOutput);
    return (
      prev.part.toolCallId === next.part.toolCallId &&
      JSON.stringify(prev.part.input) === JSON.stringify(next.part.input) &&
      outputsEqual &&
      prev.readonly === next.readonly
    );
  }
);
